import { RenderBounds, TileCoord, PixelPoint, LatLng } from './types';

const TILE_SIZE = 256;

/**
 * Coordinate utilities for Web Mercator projection and tile calculations.
 * Handles conversion between geographic coordinates, world pixels, and canvas pixels.
 */
export class CoordinateUtils {
    private bounds: RenderBounds;
    private canvasWidth: number;
    private canvasHeight: number;
    private zoom: number;

    // Precomputed values for the render bounds
    private boundsTopLeftWorld: PixelPoint;
    private worldToCanvasScale: number;

    constructor(bounds: RenderBounds, canvasWidth: number, canvasHeight: number, zoom: number) {
        this.bounds = bounds;
        this.canvasWidth = canvasWidth;
        this.canvasHeight = canvasHeight;
        this.zoom = zoom;

        // Precompute world pixel coordinates for top-left corner of bounds
        this.boundsTopLeftWorld = this.latLngToWorldPixel(bounds.north, bounds.west);
        const boundsBottomRightWorld = this.latLngToWorldPixel(bounds.south, bounds.east);

        // Calculate scale factor from world pixels to canvas pixels
        const worldWidth = boundsBottomRightWorld.x - this.boundsTopLeftWorld.x;
        const worldHeight = boundsBottomRightWorld.y - this.boundsTopLeftWorld.y;

        // Use average scale to maintain aspect ratio (should be similar if bounds are correct)
        this.worldToCanvasScale = Math.min(
            canvasWidth / worldWidth,
            canvasHeight / worldHeight
        );
    }

    /**
     * Convert lat/lng to world pixel coordinates at the current zoom level.
     * World pixels are the global pixel coordinates in the Web Mercator projection.
     */
    latLngToWorldPixel(lat: number, lng: number): PixelPoint {
        const scale = TILE_SIZE * Math.pow(2, this.zoom);
        const x = ((lng + 180) / 360) * scale;
        const latRad = lat * Math.PI / 180;
        const y = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * scale;
        return { x, y };
    }

    /**
     * Convert world pixel coordinates back to lat/lng.
     */
    worldPixelToLatLng(x: number, y: number): LatLng {
        const scale = TILE_SIZE * Math.pow(2, this.zoom);
        const lng = (x / scale) * 360 - 180;
        const latRad = Math.atan(Math.sinh(Math.PI * (1 - 2 * y / scale)));
        const lat = latRad * 180 / Math.PI;
        return { lat, lng };
    }

    /**
     * Convert lat/lng to canvas pixel coordinates.
     * This is the main function used for drawing elements on the canvas.
     */
    latLngToPixel(lat: number, lng: number): PixelPoint {
        const worldPoint = this.latLngToWorldPixel(lat, lng);
        return {
            x: (worldPoint.x - this.boundsTopLeftWorld.x) * this.worldToCanvasScale,
            y: (worldPoint.y - this.boundsTopLeftWorld.y) * this.worldToCanvasScale,
        };
    }

    /**
     * Convert canvas pixel coordinates to lat/lng.
     */
    pixelToLatLng(x: number, y: number): LatLng {
        const worldX = x / this.worldToCanvasScale + this.boundsTopLeftWorld.x;
        const worldY = y / this.worldToCanvasScale + this.boundsTopLeftWorld.y;
        return this.worldPixelToLatLng(worldX, worldY);
    }

    /**
     * Get all tile coordinates needed to cover the render bounds.
     */
    getTileCoverage(): TileCoord[] {
        const tiles: TileCoord[] = [];

        // Get tile coordinates for the corners
        const topLeftTile = this.latLngToTileCoord(this.bounds.north, this.bounds.west);
        const bottomRightTile = this.latLngToTileCoord(this.bounds.south, this.bounds.east);

        // Generate all tiles in the range
        for (let x = topLeftTile.x; x <= bottomRightTile.x; x++) {
            for (let y = topLeftTile.y; y <= bottomRightTile.y; y++) {
                tiles.push({ x, y, z: this.zoom });
            }
        }

        return tiles;
    }

    /**
     * Convert lat/lng to tile coordinates.
     */
    latLngToTileCoord(lat: number, lng: number): TileCoord {
        const x = Math.floor(((lng + 180) / 360) * Math.pow(2, this.zoom));
        const latRad = lat * Math.PI / 180;
        const y = Math.floor(
            (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * Math.pow(2, this.zoom)
        );
        return { x, y, z: this.zoom };
    }

    /**
     * Get the canvas position where a tile should be drawn.
     */
    tileToCanvasPosition(tile: TileCoord): PixelPoint {
        // Calculate the world pixel coordinates of the tile's top-left corner
        const tileWorldX = tile.x * TILE_SIZE;
        const tileWorldY = tile.y * TILE_SIZE;

        // Convert to canvas coordinates
        return {
            x: (tileWorldX - this.boundsTopLeftWorld.x) * this.worldToCanvasScale,
            y: (tileWorldY - this.boundsTopLeftWorld.y) * this.worldToCanvasScale,
        };
    }

    /**
     * Get the size a tile should be drawn at on the canvas.
     */
    getTileSizeOnCanvas(): number {
        return TILE_SIZE * this.worldToCanvasScale;
    }

    /**
     * Calculate meters per pixel at a given latitude for the current zoom level.
     */
    getMetersPerPixel(lat: number): number {
        const earthCircumference = 40075016.686;
        const metersPerPixelAtEquator = earthCircumference / (TILE_SIZE * Math.pow(2, this.zoom));
        return metersPerPixelAtEquator * Math.cos(lat * Math.PI / 180);
    }

    /**
     * Get the current zoom level.
     */
    getZoom(): number {
        return this.zoom;
    }

    /**
     * Get the render bounds.
     */
    getBounds(): RenderBounds {
        return this.bounds;
    }

    /**
     * Get canvas dimensions.
     */
    getCanvasSize(): { width: number; height: number } {
        return { width: this.canvasWidth, height: this.canvasHeight };
    }

    /**
     * Check if a lat/lng point is within the render bounds.
     */
    isInBounds(lat: number, lng: number): boolean {
        return (
            lat >= this.bounds.south &&
            lat <= this.bounds.north &&
            lng >= this.bounds.west &&
            lng <= this.bounds.east
        );
    }
}

/**
 * Calculate the appropriate zoom level for a given bounds and desired output resolution.
 * This ensures the map is rendered at the correct scale for the paper map.
 */
export function calculateZoomForBounds(
    bounds: RenderBounds,
    targetWidthPx: number,
    targetHeightPx: number
): number {
    // Calculate the center latitude for projection calculations
    const centerLat = (bounds.north + bounds.south) / 2;

    // Calculate the required ground resolution (meters per pixel)
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;

    // Approximate meters per degree at center latitude
    const metersPerDegreeLat = 111320;
    const metersPerDegreeLng = metersPerDegreeLat * Math.cos(centerLat * Math.PI / 180);

    const groundHeightMeters = latSpan * metersPerDegreeLat;
    const groundWidthMeters = lngSpan * metersPerDegreeLng;

    const metersPerPixelY = groundHeightMeters / targetHeightPx;
    const metersPerPixelX = groundWidthMeters / targetWidthPx;
    const metersPerPixel = Math.max(metersPerPixelX, metersPerPixelY);

    // Calculate zoom from meters per pixel
    // Formula: metersPerPixel = earthCircumference * cos(lat) / (256 * 2^zoom)
    const earthCircumference = 40075016.686;
    const zoom = Math.log2(
        (earthCircumference * Math.cos(centerLat * Math.PI / 180)) / (TILE_SIZE * metersPerPixel)
    );

    // Return floor to ensure we have enough resolution (slightly zoomed out is better than cropped)
    return Math.floor(zoom);
}

/**
 * Calculate zoom level for a specific map scale (e.g., 1:25000).
 *
 * @param scale - The denominator of the scale ratio (e.g., 25000 for 1:25000)
 * @param latitude - The center latitude for projection calculations
 * @param dpi - Dots per inch of the output (default 96 for screen, 300 for print)
 */
export function calculateZoomForScale(
    scale: number,
    latitude: number,
    dpi: number = 96
): number {
    // At scale 1:X, 1 meter on paper = X meters on ground
    // Pixels per meter on paper = dpi / 0.0254 (inches to meters)
    const pixelsPerMeterOnPaper = dpi / 0.0254;

    // Meters per pixel needed = scale / pixelsPerMeterOnPaper
    const metersPerPixel = scale / pixelsPerMeterOnPaper;

    // Calculate zoom from meters per pixel
    const earthCircumference = 40075016.686;
    const zoom = Math.log2(
        (earthCircumference * Math.cos(latitude * Math.PI / 180)) / (TILE_SIZE * metersPerPixel)
    );

    return zoom;
}
